import React, { useState } from "react";
import { useParams } from "react-router-dom";
import {
  GetBottleOrder,
  CreateLabOrderForBottleOrder,
  GetChainOfCustodyDoc,
  GetPackingSlipDoc,
} from "../../services/BottleOrdersService";
import OrderedBottlesTable from "../ordered-bottles/OrderedBottlesTable";
import EditBottleOrderOverView from "./EditBottleOrderOverView";
import ClientContactCard from "./ClientContactCard";
import CommonLoader from "../common/CommonLoader";
import { NotificationManager } from "react-notifications";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import {
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { PrintBottleOrderLabels } from "../../../services/barcodeService";
import { useEffect } from "react";

const EditBottleOrder = () => {
  const [loading, setLoading] = useState(false);
  const [bottleOrderData, setBottleOrderData] = useState(null);
  const [creatingLabOrder, setCreatingLabOrder] = useState(false);
  const { orderNumber } = useParams();
  const [openLabOrderDialog, setOpenLabOrderDialog] = useState(false);
  const [labOrderCount, setLabOrderCount] = useState(1);
  // Driven by the Ordered Bottles "Expand/Collapse" button: shows every order section at once
  const [sectionsExpanded, setSectionsExpanded] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem("loggedInUser")).operator;

  useEffect(() => {
    getBottleOrder(orderNumber);
  }, [orderNumber]);

  const getBottleOrder = async (orderNumber) => {
    try {
      setLoading(true);
      let response = await GetBottleOrder(orderNumber);
      setBottleOrderData(response.results);
    } catch (err) {
      NotificationManager.error("Error getting bottle order. " + err, "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLabOrder = async () => {
    try {
      setCreatingLabOrder(true);

      await CreateLabOrderForBottleOrder(orderNumber, labOrderCount);

      NotificationManager.success(
        `${labOrderCount} lab order(s) created successfully`,
        "Success"
      );

      setOpenLabOrderDialog(false);
      window.location.reload();
    } catch (err) {
      NotificationManager.error("Error creating lab order. " + err, "Error");
    } finally {
      setCreatingLabOrder(false);
    }
  };

  const handlePrintCoC = async () => {
    try {
      // setLoading(true);
      await GetChainOfCustodyDoc(orderNumber);
      NotificationManager.success("Chain of Custody downloaded successfully");
    } catch (err) {
      NotificationManager.error("Error printing Chain of Custody. " + err, "Error");
    } finally {
      // setLoading(false);
    }
  };

  const handleBottleLabelsPrint = async () => {
    try {
      // setLoading(true);
      await PrintBottleOrderLabels("sampleLabel", orderNumber);
      NotificationManager.success("Bottle labels printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing bottle labels");
    } finally {
      // setLoading(false);
    }
  };

  const handleBottleBarCodePrint = async () => {
    try {
      // setLoading(true);
      await PrintBottleOrderLabels("sampleBarcode", orderNumber);
      NotificationManager.success("Barcode labels printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing barcode labels");
    } finally {
      // setLoading(false);
    }
  };

  const handlePrintPackingSlip = async () => {
    try {
      // setLoading(true);
      await GetPackingSlipDoc(orderNumber);
      NotificationManager.success("Packing Slip downloaded successfully");
    } catch (err) {
      NotificationManager.error("Error printing Packing Slip. " + err, "Error");
    } finally {
      // setLoading(false);
    }
  };

  return (
    <div className="vlp-component-container">
      <span className="title">
        Edit Bottle Order{" "}
        <span style={{ color: "rgb(25,118,210)", fontWeight: "bold" }}>
          {orderNumber}
        </span>
      </span>
      <hr />
      <div
        style={{
          display: "flex",
          gap: "16px",
          marginTop: "20px",
          marginBottom: "20px",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="outlined"
          onClick={() => setOpenLabOrderDialog(true)}
          disabled={!orderNumber || creatingLabOrder}
        >
          {creatingLabOrder ? "Creating..." : "Create Lab Order"}
        </Button>

        <Button
          variant="outlined"
          onClick={handlePrintCoC}
          disabled={loading}
          startIcon={<LocalPrintshopIcon />}
        >
          Chain of Custody
        </Button>

        <Button
          variant="outlined"
          onClick={handlePrintPackingSlip}
          disabled={loading}
          startIcon={<LocalPrintshopIcon />}
        >
          Packing Slip
        </Button>

        <Button
          variant="outlined"
          onClick={handleBottleLabelsPrint}
          disabled={loading}
          startIcon={<LocalPrintshopIcon />}
          sx={{ color: "#0eecf0", borderColor: "#0eecf0" }}
        >
          Bottle Labels
        </Button>

        <Button
          variant="outlined"
          onClick={handleBottleBarCodePrint}
          disabled={loading}
          startIcon={<QrCodeIcon />}
          sx={{ color: "#8911f2", borderColor: "#8911f2" }}
        >
          Barcode Labels
        </Button>
      </div>
      <Divider sx={{ mb: 2 }} />

      {bottleOrderData && (
        <>
          <ClientContactCard bottleOrderData={bottleOrderData} />
          <OrderedBottlesTable
            orderedBottles={bottleOrderData.orderedBottles}
            orderNumber={orderNumber}
            accountNumber={bottleOrderData.accountNumber}
            onExpandAllChange={setSectionsExpanded}
          />
          <EditBottleOrderOverView
            bottleOrderData={bottleOrderData}
            currentUser={currentUser}
            expandAll={sectionsExpanded}
          />
        </>
      )}

      <Dialog
        open={openLabOrderDialog}
        onClose={() => setOpenLabOrderDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Lab Orders</DialogTitle>

        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Number of Orders"
            type="number"
            fullWidth
            variant="outlined"
            value={labOrderCount}
            onChange={(e) => {
              const value = Number(e.target.value);
              setLabOrderCount(value);
            }}
            inputProps={{ min: 1, max: 20 }}
            error={labOrderCount < 1 || labOrderCount > 20}
            helperText={
              labOrderCount < 1
                ? "Minimum 1 order required"
                : labOrderCount > 20
                  ? "Maximum 20 orders allowed"
                  : "Enter a number between 1 and 20"
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenLabOrderDialog(false)}>
            Cancel
          </Button>

          <Button
            onClick={handleCreateLabOrder}
            variant="contained"
            disabled={creatingLabOrder || (labOrderCount < 1 || labOrderCount > 20)}
          >
            {creatingLabOrder ? "Creating..." : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
      <CommonLoader loading={loading} />
    </div>
  );
};

export default EditBottleOrder;